import React, { useCallback } from 'react';
import {
  Paper,
  Box,
  Chip,
  Tooltip,
  IconButton,
  Grid,
  TextField,
  Divider,
  Stack,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import HelpIcon from '../../help/HelpIcon';

const HELP_TOPIC = { module: 'modelo-semantico', topic: 'tabla-semantica' };

const outlinedInputSx = {
  color: 'var(--wb-text)',
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue-strong)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue-light)' }
};

const JoinConditionRow = ({
  joinIndex,
  index,
  condition,
  labelColor,
  onConditionChange,
  onRemove,
  disableRemove
}) => {
  const handleFieldChange = useCallback(
    (field) => (event) => {
      onConditionChange(joinIndex, index, field, event.target.value);
    },
    [joinIndex, index, onConditionChange]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: 'var(--wb-x-bg-111827, #111827)',
        border: '1px dashed var(--wb-x-border-3b82f659, rgba(59, 130, 246, 0.35))',
        borderRadius: 0,
        p: 2
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          label="Columna origen"
          value={condition.source_column}
          onChange={handleFieldChange('source_column')}
          size='small'
          InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
          InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } } }}
        />
        <TextField
          label="Columna destino"
          value={condition.target_column}
          onChange={handleFieldChange('target_column')}
          size='small'
          InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
          InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } } }}
        />
        <HelpIcon {...HELP_TOPIC} anchor="condiciones-columna-origen--columna-destino" />
        <Tooltip title="Eliminar condición">
          <span>
            <IconButton
              onClick={() => onRemove(joinIndex, index)}
              size='small'
              sx={{ color: 'var(--wb-accent)' }}
              disabled={disableRemove}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
};

const JoinCard = ({
  join,
  index,
  labelColor,
  accentColor,
  onFieldChange,
  onConditionChange,
  onAddCondition,
  onRemoveCondition,
  onRemoveJoin
}) => {
  const handleFieldChange = useCallback(
    (field) => (event) => {
      onFieldChange(index, field, event.target.value);
    },
    [index, onFieldChange]
  );

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: 'var(--wb-x-bg-1c2436, #1c2436)',
        border: '1px solid var(--wb-x-border-3b82f659, rgba(59, 130, 246, 0.35))',
        borderRadius: 0,
        p: 3,
        position: 'relative'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          display: 'flex',
          gap: 1
        }}
      >
        <Chip
          label={'Join ' + (index + 1)}
          size='small'
          sx={{ bgcolor: 'var(--wb-accent-18)', color: 'var(--wb-text-label)' }}
        />
        <Tooltip title="Eliminar join">
          <span>
            <IconButton
              onClick={() => onRemoveJoin(index)}
              size='small'
              sx={{ color: 'var(--wb-accent)' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={4}>
          <TextField
            label="Tabla destino"
            value={join.target_table}
            onChange={handleFieldChange('target_table')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="tabla--esquema--base-de-datos-destino" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Esquema destino"
            value={join.target_schema}
            onChange={handleFieldChange('target_schema')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="tabla--esquema--base-de-datos-destino" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Base destino"
            value={join.target_database}
            onChange={handleFieldChange('target_database')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="tabla--esquema--base-de-datos-destino" /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <FormControl fullWidth>
              <InputLabel sx={labelColor}>Tipo de join</InputLabel>
              <Select
                label="Tipo de join"
                value={join.type}
                onChange={handleFieldChange('type')}
                sx={{ color: 'var(--wb-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue-strong)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue-light)' }, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }}
              >
                <MenuItem value="left">Left</MenuItem>
                <MenuItem value="inner">Inner</MenuItem>
                <MenuItem value="right">Right</MenuItem>
                <MenuItem value="full">Full</MenuItem>
              </Select>
            </FormControl>
            <HelpIcon {...HELP_TOPIC} anchor="tipo-de-join" sx={{ mt: 1 }} />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <FormControl fullWidth>
              <InputLabel sx={labelColor}>Cardinalidad</InputLabel>
              <Select
                label="Cardinalidad"
                value={join.cardinality}
                onChange={handleFieldChange('cardinality')}
                sx={{ color: 'var(--wb-text)', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue-strong)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue)' }, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--wb-blue-light)' }, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }}
              >
                <MenuItem value="">No especificar</MenuItem>
                <MenuItem value="one_to_one">Uno a uno</MenuItem>
                <MenuItem value="one_to_many">Uno a muchos</MenuItem>
                <MenuItem value="many_to_one">Muchos a uno</MenuItem>
                <MenuItem value="many_to_many">Muchos a muchos</MenuItem>
              </Select>
            </FormControl>
            <HelpIcon {...HELP_TOPIC} anchor="cardinalidad" sx={{ mt: 1 }} />
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            label="Propósito"
            value={join.purpose}
            onChange={handleFieldChange('purpose')}
            fullWidth
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{ sx: { ...outlinedInputSx, '& .MuiOutlinedInput-input': { py: '6px', px: '12px' } }, endAdornment: <InputAdornment position="end"><HelpIcon {...HELP_TOPIC} anchor="propósito" /></InputAdornment> }}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2, borderColor: 'var(--wb-x-border-38bdf859, rgba(56, 189, 248, 0.35))' }} />

      <Stack spacing={2}>
        {(join.on || []).map((condition, conditionIndex) => (
          <JoinConditionRow
            key={'condition-' + conditionIndex}
            joinIndex={index}
            index={conditionIndex}
            condition={condition}
            labelColor={labelColor}
            onConditionChange={onConditionChange}
            onRemove={onRemoveCondition}
            disableRemove={(join.on || []).length === 1}
          />
        ))}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => onAddCondition(index)}
          sx={{ alignSelf: 'flex-start', borderColor: accentColor, color: 'var(--wb-accent)' }}
        >
          Agregar condición
        </Button>
      </Stack>
    </Paper>
  );
};

export { JoinCard, JoinConditionRow };
